import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  ParsedTournament,
  Tournament,
  TournamentStatus,
  TournamentType,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

export type TopdeckPendingStatus = 'PENDING' | 'IMPORTED' | 'DISMISSED';

export type TopdeckPendingPlayer = {
  id: number;
  topdeckUid: string;
  rank: number | null;
  wins: number | null;
  losses: number | null;
  draws: number | null;
  user: {
    id: number;
    name: string;
    lastName: string;
    cossyId: string | null;
    topDeckId: string | null;
  };
};

/** Por qué un torneo entró como posible duplicado de un pendiente. */
export type DuplicateMatchReason = 'PLAYERS' | 'ORGANIZER' | 'NAME';

export type TopdeckDuplicateCandidate = {
  tournament: {
    id: number;
    title: string;
    dateFrom: string | null;
    status: TournamentStatus;
  };
  matchedPlayers: number;
  pendingPlayers: number;
  coverage: number;
  nameSimilarity: number;
  reasons: DuplicateMatchReason[];
};

export type TopdeckPendingTournament = {
  id: number;
  topdeckId: string;
  name: string;
  format: string | null;
  startDate: string;
  participants: number;
  city: string | null;
  state: string | null;
  country: string | null;
  status: TopdeckPendingStatus;
  detectedAt: string;
  processedAt: string | null;
  tournament: Tournament | null;
  players: TopdeckPendingPlayer[];
  possibleDuplicates: TopdeckDuplicateCandidate[];
};

export type TopdeckDetectionResult = {
  scanned: number;
  matched: number;
  created: number;
};

const PENDING_FIELDS = gql`
  fragment TopdeckPendingFields on TopdeckPendingTournament {
    id
    topdeckId
    name
    format
    startDate
    participants
    city
    state
    country
    status
    detectedAt
    processedAt
    # Los campos extra son los que pide getTournamentDisplayStatus: sin ellos
    # un torneo importado que quedó "Sin Cerrar" se ve igual que uno cerrado.
    tournament {
      id
      title
      status
      dateFrom
      dateTo
      rounds
      currentRound
      tournamentType
      deletedAt
    }
    players {
      id
      topdeckUid
      rank
      wins
      losses
      draws
      user {
        id
        name
        lastName
        cossyId
        topDeckId
      }
    }
    possibleDuplicates {
      tournament {
        id
        title
        dateFrom
        status
      }
      matchedPlayers
      pendingPlayers
      coverage
      nameSimilarity
      reasons
    }
  }
`;

export const getTopdeckPending = async (
  user: Nullable<UserWithToken>,
  status?: TopdeckPendingStatus
) =>
  getGraphQLQueryClient(user).request<WithData<TopdeckPendingTournament[]>>(
    gql`
      ${PENDING_FIELDS}
      query topdeckPendingTournaments($status: TopdeckPendingStatus) {
        data: topdeckPendingTournaments(status: $status) {
          ...TopdeckPendingFields
        }
      }
    `,
    { status: status ?? null }
  );

export const detectTopdeckTournaments = async (
  user: Nullable<UserWithToken>,
  days: number
) =>
  getGraphQLQueryClient(user).request<WithData<TopdeckDetectionResult>>(
    gql`
      mutation detectTopdeckTournaments($days: Int) {
        data: detectTopdeckTournaments(days: $days) {
          scanned
          matched
          created
        }
      }
    `,
    { days }
  );

export const dismissTopdeckPending = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<TopdeckPendingTournament>>(
    gql`
      ${PENDING_FIELDS}
      mutation dismissTopdeckPending($id: Int!) {
        data: dismissTopdeckPending(id: $id) {
          ...TopdeckPendingFields
        }
      }
    `,
    { id }
  );

export const linkTopdeckPending = async (
  user: Nullable<UserWithToken>,
  id: number,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TopdeckPendingTournament>>(
    gql`
      ${PENDING_FIELDS}
      mutation linkTopdeckPending($id: Int!, $tournamentId: Int!) {
        data: linkTopdeckPending(id: $id, tournamentId: $tournamentId) {
          ...TopdeckPendingFields
        }
      }
    `,
    { id, tournamentId }
  );

export const importTopdeckPending = async (
  user: Nullable<UserWithToken>,
  id: number,
  /** Tienda del torneo nuevo. Se omite cuando se importa sobre uno existente. */
  organizerId: Nullable<number>,
  /**
   * Torneo ya cargado sobre el que importar —el del calendario—. Sin esto el
   * API crea un torneo nuevo.
   */
  tournamentId?: number,
  /** Tipo del torneo nuevo; por defecto suizo. Se ignora con `tournamentId`. */
  tournamentType?: TournamentType
) =>
  getGraphQLQueryClient(user).request<
    WithData<{
      tournament: Tournament;
      pending: TopdeckPendingTournament;
      importData: Nullable<ParsedTournament>;
    }>
  >(
    gql`
      ${PENDING_FIELDS}
      mutation importTopdeckPending(
        $id: Int!
        $organizerId: Int
        $tournamentId: Int
        $tournamentType: TournamentType
      ) {
        data: importTopdeckPending(
          id: $id
          organizerId: $organizerId
          tournamentId: $tournamentId
          tournamentType: $tournamentType
        ) {
          tournament {
            id
            title
            topdeckId
          }
          pending {
            ...TopdeckPendingFields
          }
          importData
        }
      }
    `,
    { id, organizerId, tournamentId, tournamentType }
  );
